import { ConnectionPositionPair } from '@angular/cdk/overlay';
import { NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment-timezone';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

import type { UserBase } from '@recruitee/account-types';
import { trackByProp } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { InterviewResultRequestActions } from '@recruitee/interview-result-request-data';
import type { InterviewResultRequest } from '@recruitee/interview-result-request-types';
import { AppNavigatorService } from '@recruitee/navigation';
import {
  ConnectedOverlayDirective,
  DialogRef,
  DialogService,
  DialogWidth,
  NotificationsController,
  OverlayOriginDirective,
  RtStatefulComponent,
  DateComponent,
  OfferStatusIndicatorComponent,
  RtTooltip,
  HeadingComponent,
  AvatarComponent,
  IconComponent,
  IconColorDirective,
  BoxComponent,
  DropdownItemComponent,
  DropdownHeaderComponent,
  DropdownComponent,
  IconButtonComponent,
  GroupComponent,
  TruncateComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  ScrollableDirective,
} from '@recruitee/user-interface';

type State = {
  meetingDate: string | null;
  loading: string | boolean;
};

@Component({
  selector: 'rt-interview-result-request-card',
  templateUrl: 'interview-result-request-card.component.html',
  styleUrls: ['./interview-result-request-card.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    BoxComponent,
    IconColorDirective,
    IconComponent,
    AvatarComponent,
    HeadingComponent,
    RtTooltip,
    OfferStatusIndicatorComponent,
    DateComponent,
    GroupComponent,
    NgTemplateOutlet,
    IconButtonComponent,
    ButtonLoadingDirective,
    TruncateComponent,
    ButtonComponent,
    OverlayOriginDirective,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownHeaderComponent,
    ScrollableDirective,
    DropdownItemComponent,
    UpperCasePipe,
    TranslatePipe,
  ],
})
export class InterviewResultRequestCardComponent extends RtStatefulComponent<State> {
  @Input() public request: InterviewResultRequest;
  @Input() public candidateId: number;
  @Input() public short: boolean = false;
  @Input() public dialogRef: DialogRef;
  @Input() public inDashboard: boolean = false;
  @Input() public inCandidate: boolean = false;
  @Input() public openCandidateOnClick: boolean = false;
  @Input() public openEventModalOnClick: boolean = false;
  @Input() public currentAdmin: UserBase;
  @Input() public hasHighLevelDataAccess: boolean = false;

  @ViewChild('selectActionDropdown')
  public selectActionDropdown: ConnectedOverlayDirective;
  @ViewChild('dropdownOrigin')
  public dropdownOrigin: OverlayOriginDirective;

  public trackByOfferId = trackByProp({ prop: 'offerId' });
  public dropdownConfig: ConnectionPositionPair[] = [
    {
      originX: 'end',
      originY: 'bottom',
      overlayX: 'end',
      overlayY: 'top',
    },
    {
      originX: 'end',
      originY: 'top',
      overlayX: 'end',
      overlayY: 'bottom',
    },
  ];

  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private dialogService: DialogService,
    private viewContainerRef: ViewContainerRef,
    private i18n: I18nService,
    private notificationsCtrl: NotificationsController,
    private appNavigator: AppNavigatorService,
    private store: Store,
    private actions$: Actions,
    protected cdRef: ChangeDetectorRef,
  ) {
    super(cdRef);
    this.createState({
      meetingDate: null,
      loading: false,
    });
  }

  public ngOnInit(): void {
    if (!this.short && this.request.interviewEvent) {
      const date = moment(this.request.interviewEvent.startsAt);
      const duration = this.request.interviewEvent.duration;

      date.tz(this.request.interviewEvent.timezone);

      this.setState({
        ...this.state,
        meetingDate: `${date.format('DD MMM YYYY')}, ${date.format('LT')} - ${date
          .add(duration, 'm')
          .format('LT')}`,
      });
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public isOwn(admin: { id: number }): boolean {
    return admin && this.currentAdmin.id === admin.id;
  }

  public isCreatedByAdmin(): boolean {
    return this.request.requesterId === this.currentAdmin.id;
  }

  public dismiss(event: MouseEvent, id: number): void {
    event.stopPropagation();
    this.setState({
      ...this.state,
      loading: 'dismiss',
    });

    this.store.dispatch(InterviewResultRequestActions.dismiss({ payload: { id } }));
    this.actions$
      .pipe(
        ofType(InterviewResultRequestActions.dismissSuccess),
        filter(({ payload }) => payload.data.id === id),
        take(1),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(() => {
        const message = this.i18n.translate('app.interview.pending_dismiss_notification');
        this.notificationsCtrl.success(message);
        this.setState({
          ...this.state,
          loading: false,
        });
      });
  }

  public revert(event: MouseEvent, id: number): void {
    event.stopPropagation();
    this.setState({
      ...this.state,
      loading: 'revert',
    });
    this.store.dispatch(InterviewResultRequestActions.revert({ payload: { id } }));
    this.actions$
      .pipe(
        ofType(InterviewResultRequestActions.revertSuccess),
        filter(({ payload }) => payload.data.id === id),
        take(1),
        takeUntil(this.ngOnDestroy$),
      )
      .subscribe(() => {
        const message = this.i18n.translate('app.interview.pending_revert_notification');
        this.notificationsCtrl.success(message);
        this.setState({
          ...this.state,
          loading: false,
        });
      });
  }

  public delete(event: MouseEvent, id: number): void {
    event.stopPropagation();
    const options = {
      title: this.i18n.translate('app.common.confirm_delete'),
      message: this.i18n.translate('app.interview.pending_delete_confirmation'),
      variant: 'error',
      isCancelable: true,
    };

    this.dialogService.confirm(options, this.viewContainerRef).subscribe(confirmed => {
      if (confirmed) {
        this.setState({
          ...this.state,
          loading: 'delete',
        });
        this.store.dispatch(
          InterviewResultRequestActions.removeOnePessimistic({ payload: { data: id } }),
        );
        this.actions$
          .pipe(
            ofType(InterviewResultRequestActions.removeOnePessimisticSuccess),
            filter(({ payload }) => payload.data.id === id),
            take(1),
            takeUntil(this.ngOnDestroy$),
          )
          .subscribe(() => {
            const message = this.i18n.translate('app.interview.pending_delete_notification');
            this.notificationsCtrl.success(message);
            this.setState({
              ...this.state,
              loading: false,
            });
          });
      }
    });
  }

  public startInterview(event: MouseEvent): void {
    event.stopPropagation();

    if (
      this.request.candidatePlacements?.some(
        placement =>
          (placement.offerId || placement.talentPoolId) ===
          (this.request.offerId || this.request.talentPoolId),
      )
    ) {
      this.openCandidateDialogWithInterview(this.request.offerId || this.request.talentPoolId!);
    } else if (this.request.candidatePlacements?.length) {
      this.selectActionDropdown.openAt(this.dropdownOrigin);
    } else {
      this.openCandidateDialogWithInterview();
    }
  }

  public isStatus(status: string): boolean {
    return this.request.status === status;
  }

  public openCandidate(request: InterviewResultRequest): void {
    this.appNavigator.openCandidateDialog(request.candidateId, {
      tab: 'interviews',
    });
  }

  public openEventModal(event: MouseEvent, request: InterviewResultRequest): void {
    event.stopPropagation();

    this.appNavigator.openInterviewEventDialog(request.interviewEventId!, {
      config: { width: DialogWidth.SmallX },
    });
  }

  public openCandidateDialogWithInterview(offerId?: number) {
    this.appNavigator.openCandidateDialog(this.candidateId || this.request.candidateId, {
      openInterview: true,
      interviewOfferId: offerId,
      interviewTemplateId: this.request.interviewTemplateId,
      interviewResultRequestId: this.request.id,
    });

    if (this.dialogRef) {
      this.dialogRef.close();
    }
  }
}
